import React, { useState, useEffect } from 'react';
import { Bell, Check, Loader2 } from 'lucide-react';
import { useNotificationPreferences, useUpdateNotificationPreferences } from '../../hooks';
import { ToggleField } from '../ui/toggle';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Card } from '../ui/card';

const notificationOptions = [
  {
    key: 'email_new_applicant',
    label: 'New Applicant Created',
    description: 'Get notified when a new applicant is created',
  },
  {
    key: 'email_review_required',
    label: 'Review Required',
    description: 'Get notified when an applicant needs review',
  },
  {
    key: 'email_high_risk_alert',
    label: 'High Risk Alerts',
    description: 'Get notified when a high-risk applicant is detected',
  },
  {
    key: 'email_screening_hit',
    label: 'Screening Hits',
    description: 'Get notified when screening finds a potential match',
  },
  {
    key: 'email_case_assigned',
    label: 'Case Assignments',
    description: 'Get notified when a case is assigned to you',
  },
  {
    key: 'email_daily_digest',
    label: 'Daily Digest',
    description: 'Receive a daily summary of activity',
  },
  {
    key: 'email_weekly_report',
    label: 'Weekly Report',
    description: 'Receive a weekly compliance summary report',
  },
];

export default function NotificationSettings() {
  const { data: preferences, isLoading } = useNotificationPreferences();
  const updatePreferences = useUpdateNotificationPreferences();

  const [formData, setFormData] = useState({
    email_new_applicant: true,
    email_review_required: true,
    email_high_risk_alert: true,
    email_screening_hit: true,
    email_case_assigned: true,
    email_daily_digest: false,
    email_weekly_report: true,
  });
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Initialize from server data
  useEffect(() => {
    if (preferences) {
      setFormData(preferences);
    }
  }, [preferences]);

  const handleToggle = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    try {
      await updatePreferences.mutateAsync(formData);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save notification preferences:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" className="h-5 w-48" />
        </div>
        <Skeleton variant="text" className="h-4 w-64" />
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rectangular" className="h-[72px] w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-grey-900 dark:text-grey-50 m-0 flex items-center gap-2">
          <Bell className="w-5 h-5 text-teal-500" />
          Notification Preferences
        </h2>
        <p className="text-sm text-grey-500 dark:text-grey-400 mt-2">
          Choose which email notifications you'd like to receive.
        </p>
      </div>

      {/* Notification List */}
      <div className="space-y-3">
        {notificationOptions.map((option) => (
          <Card key={option.key} variant="outline" className="p-4">
            <ToggleField
              label={option.label}
              hint={option.description}
              checked={formData[option.key]}
              onCheckedChange={(value) => handleToggle(option.key, value)}
              disabled={updatePreferences.isPending}
            />
          </Card>
        ))}
      </div>

      {/* Save Button */}
      <Button
        variant={saveSuccess ? 'success' : 'default'}
        size="lg"
        onClick={handleSave}
        disabled={updatePreferences.isPending}
      >
        {updatePreferences.isPending ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Saving...
          </>
        ) : saveSuccess ? (
          <>
            <Check className="w-4 h-4" />
            Saved!
          </>
        ) : (
          'Save Preferences'
        )}
      </Button>
    </div>
  );
}
